import {ChangeDetectionStrategy, Component, OnInit, computed, inject, Signal} from '@angular/core';
import {Router, RouterOutlet} from '@angular/router';
import {LoadingIndicatorComponent} from "./modules/helper/loading-indicator/loading-indicator.component";
import {AsyncPipe} from "@angular/common";
import {toSignal} from '@angular/core/rxjs-interop';
import {map, startWith} from 'rxjs';
import {LanguageService} from "./shared/services/language.service";
import {DialogDocumentsGalleryComponent} from "./modules/documents/dialog-documents-gallery/dialog-documents-gallery.component";
import {DocumentService} from "./shared/services/document.service";
import {DialogProfileAvatarComponent} from "./modules/profile/dialog-profile-avatar/dialog-profile-avatar.component";
import {ProfileService} from "./shared/services/profile.service";
import {SyncfusionTranslationService} from './shared/services/syncfusion-translation.service';
import {LayoutService} from './shared/services/layout.service';
import {EmojiPickerComponent} from "./modules/helper/emoji-picker/emoji-picker.component";
import {DialogChatForwardComponent} from "./modules/chat/dialog-chat-forward/dialog-chat-forward.component";
import * as Sentry from "@sentry/angular";
import {GlobalFooterComponent} from "./modules/helper/bar-global-footer/bar-global-footer.component";
import {BarTopComponent} from "./modules/helper/bar-top/bar-top.component";
import {ModuleConfigurationService} from './shared/services/module-configuration.service';
import {BarIconsComponent} from './modules/helper/bar-icons/bar-icons.component';
import {IconBarPolicy, IconBarService} from './shared/services/icon-bar.service';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, LoadingIndicatorComponent, AsyncPipe, DialogDocumentsGalleryComponent, DialogProfileAvatarComponent, EmojiPickerComponent, DialogChatForwardComponent, GlobalFooterComponent, BarTopComponent, BarIconsComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  public languageService: LanguageService = inject(LanguageService);
  public documentService: DocumentService = inject(DocumentService);
  public profileService: ProfileService = inject(ProfileService);
  private moduleConfigurationService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private barIconService: IconBarService = inject(IconBarService);
  private router: Router = inject(Router);
  // Required to activate Angular router tracing in Sentry.
  private sentryTraceService: Sentry.TraceService = inject(Sentry.TraceService);
  private syncfTranslations: SyncfusionTranslationService = inject(SyncfusionTranslationService);
  // injecting layout service here is necessary to setup the application theme
  private layoutService: LayoutService = inject(LayoutService);
  private currentUrl: Signal<string> = toSignal(
    this.router.events.pipe(
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    {initialValue: this.router.url}
  );

  public showTopMenu: Signal<boolean> = computed(
    () => this.moduleConfigurationService.shouldShowTopMenuForRoute(this.currentUrl())
  );

  private iconBarPolicy: Signal<IconBarPolicy> = toSignal(
    this.barIconService.iconBarPolicy,
    {initialValue: IconBarPolicy.Toggleable}
  );

  public showBarIcons: Signal<boolean> = computed(() => {
    const currentUrl = this.currentUrl();

    if (this.isRoute(currentUrl, '/calendar')) {
      return false;
    }

    if (this.isRoute(currentUrl, '/home')) {
      return this.iconBarPolicy() === IconBarPolicy.ForcedVisible;
    }

    return this.moduleConfigurationService.shouldShowMenuForRoute(currentUrl);
  });

  public ngOnInit(): void {
    void this.sentryTraceService;
    this.syncfTranslations.setSyncFusionTranslation();
  }

  private isRoute(route: string, routePrefix: string): boolean {
    const path = route.split(/[?#]/)[0]?.trim().replace(/;[^/]*/g, '') ?? '';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return normalizedPath === routePrefix || normalizedPath.startsWith(`${routePrefix}/`);
  }
}
